import { HistoryRecordAction, type AdjustmentHistoryInfo } from "@/lib/catalog/types"
import {
	historyMessageKeys,
	historyControlNameKeys,
	getMaskDisplayName,
	getPackagedPresetDisplayName,
} from "./dual-role-strings"
import { getLocalizedStyleDisplayName } from "./style-display-name"

type ComponentsTFunction = (key: string, params?: Record<string, unknown>) => string

const localizeStyleName = (name: string, t: ComponentsTFunction): string => {
	return getLocalizedStyleDisplayName(name, (key) => t(key))
}

const localizePresetName = (name: string, t: ComponentsTFunction): string => {
	return getPackagedPresetDisplayName(name, (key, options) => t(key, options))
}

export const getHistoryChangedValueDisplay = (
	record: AdjustmentHistoryInfo,
	t: ComponentsTFunction,
): string | undefined => {
	if (!record.changedValue) {
		return undefined
	}

	if (record.action === HistoryRecordAction.ApplyAIStyle) {
		return localizeStyleName(record.changedValue, t)
	}

	return record.changedValue
}

/** Resolve a stored mask name to its translated display string. */
const translateMaskName = (name: string, t: ComponentsTFunction): string => {
	const result = getMaskDisplayName(name)
	if (!result) return name
	const params = { ...result.params }
	if (typeof params.typeKey === "string") params.type = t(params.typeKey)
	if (typeof params.partKey === "string") params.part = t(params.partKey)
	return t(result.key, params)
}

/**
 * Translate a history record's message for display.
 * Used by both HistoryPanel (UI) and store-history toast (store layer).
 */
export function translateHistoryMessage(
	record: AdjustmentHistoryInfo,
	t: ComponentsTFunction,
): string {
	switch (record.action) {
		case HistoryRecordAction.AutoEnhance:
		case HistoryRecordAction.PasteAdjustments:
		case HistoryRecordAction.ApplyColorMatch:
		case HistoryRecordAction.Reset:
		case HistoryRecordAction.Import:
			return t(historyMessageKeys[record.action])
		case HistoryRecordAction.ApplyPreset: {
			const rawPresetName = record.message.replace("Apply Preset: ", "") || record.message // i18n-ignore: parsing stored history key
			const presetName = localizePresetName(rawPresetName, t)
			return t(historyMessageKeys[record.action], { presetName })
		}
		case HistoryRecordAction.ApplyAIStyle: {
			return t(historyMessageKeys[record.action])
		}
		default: {
			const key = historyControlNameKeys[record.message]
			if (key) return t(key)

			// "Add Subject Mask" → "Add {{maskType}} Mask"
			const addMaskMatch = record.message.match(/^Add (\w+) Mask$/) // i18n-ignore
			if (addMaskMatch) {
				return t("historyPanel.maskAdd", {
					maskType: translateMaskName(addMaskMatch[1] ?? "", t),
				})
			}

			// "Show/Hide MaskName" → translated verb + translated mask name
			const toggleMatch = record.message.match(/^(Show|Hide) (.+)$/) // i18n-ignore
			if (toggleMatch) {
				const verb =
					toggleMatch[1] === "Show"
						? t("historyPanel.maskShow")
						: t("historyPanel.maskHide")
				const maskName = translateMaskName(toggleMatch[2] ?? "", t)
				return `${verb} ${maskName}`
			}

			// "MaskName: Slider" → translated mask name + translated slider
			const maskSliderMatch = record.message.match(/^(.+): (.+)$/) // i18n-ignore
			if (maskSliderMatch) {
				const maskName = translateMaskName(maskSliderMatch[1] ?? "", t)
				const sliderKey = historyControlNameKeys[maskSliderMatch[2] ?? ""]
				const slider = sliderKey ? t(sliderKey) : (maskSliderMatch[2] ?? "")
				return `${maskName}: ${slider}`
			}

			return record.message
		}
	}
}
